import * as React from "react";
import {
  Box,
  Button,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Project } from "../../types/project";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";
import { useCancelProjectMutation } from "../../services/project.service";
import SelectReason from "../../components/SelectReason";
import { BaseModal } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalCancelProjectProps {
  open: boolean;
  onClose: () => void;
  project: Project | null;
}

const ModalCancelProject = ({
  open,
  onClose,
  project,
}: ModalCancelProjectProps) => {
  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const [cancel, { isLoading }] = useCancelProjectMutation();

  // UI Labels
  const cancelProjectLabel = useUILabel("project.cancelProject");
  const selectCancelReasonLabel = useUILabel("project.selectCancelReason");
  const projectCanceledLabel = useUILabel("project.projectCanceled");
  const cancelLabel = useUILabel("common.cancel");
  const submitLabel = useUILabel("common.submit");
  const thisFieldIsRequiredLabel = useUILabel("common.thisFieldIsRequired");

  const onSubmit = async (payload: any) => {
    try {
      if (!project) return;

      await cancel({ projectId: project.id, reason: payload.reason }).unwrap();
      toast.success(projectCanceledLabel);
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("reason", "");
      clearErrors();
    }
  }, [open, setValue, clearErrors]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={isLoading}>
        {cancelLabel}
      </Button>
      <LoadingButton
        form="cancel-project-form"
        type="submit"
        variant="contained"
        loading={isLoading}
      >
        {submitLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={cancelProjectLabel}
      footer={footer}
      maxWidth="xs"
      useGlassCard={true}
    >
      <Box
        component="form"
        id="cancel-project-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ 
          p: 3,
          "@media (orientation: landscape) and (max-width: 900px)": {
            p: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Controller
          name="reason"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <SelectReason
              label={selectCancelReasonLabel}
              onChange={onChange}
              error={!!errors.reason}
              helperText={!!errors.reason && thisFieldIsRequiredLabel}
              type="cancel-job"
            />
          )}
        />
      </Box>
    </BaseModal>
  );
};
export default ModalCancelProject;
